import React, { useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import {
  MdAdd,
  MdBolt,
  MdCardGiftcard,
  MdClose,
  MdDeleteOutline,
  MdEdit,
  MdEmojiEvents,
  MdFormatListNumbered,
  MdGroups,
  MdPerson,
  MdSavings,
  MdTrendingUp,
  MdStairs,
  MdAlarmOn,
  MdFastForward,
  MdTaskAlt,
  MdRemoveCircleOutline,
} from "react-icons/md";
import Sidebar from "../Sidebar";
import Layout from "../../layout/Layout";
import Modal from "../Model";
import GameRulesTab from "./GameRulesTab";
import EotmTab from "./EotmTab";
import {
  ADJUST_REWARD_COINS,
  DELETE_REWARD_RULE,
  GET_EMPLOYEES,
  GET_REWARD_LEDGER,
  GET_REWARD_RULES,
  SAVE_REWARD_RULE,
  SET_REWARD_RULE_ACTIVE,
} from "./graphql";

const RULE_TYPES = [
  {
    value: "PER_BOOKING",
    label: "Every booking",
    hint: "Pays each time a booking is completed",
    Icon: MdBolt,
    tone: "bg-amber-50 text-amber-600 ring-amber-200",
  },
  {
    value: "BOOKINGS_MILESTONE",
    label: "Booking count",
    hint: "Pays once when N bookings are completed in a day, week or month",
    Icon: MdFormatListNumbered,
    tone: "bg-violet-50 text-violet-600 ring-violet-200",
  },
  {
    value: "REVENUE_MILESTONE",
    label: "Amount earned",
    hint: "Pays once when completed bookings add up to ₹N in a day, week or month",
    Icon: MdTrendingUp,
    tone: "bg-emerald-50 text-emerald-600 ring-emerald-200",
  },
  {
    value: "ON_TIME_START",
    label: "Started on time",
    hint: "Pays per booking started within the grace minutes of the slot",
    Icon: MdAlarmOn,
    tone: "bg-sky-50 text-sky-600 ring-sky-200",
  },
  {
    value: "EARLY_START",
    label: "Started early",
    hint: "Pays per booking started before the slot time",
    Icon: MdFastForward,
    tone: "bg-indigo-50 text-indigo-600 ring-indigo-200",
  },
  {
    value: "ON_TIME_FINISH",
    label: "Finished on time",
    hint: "Pays per booking finished within its booked time (+ grace)",
    Icon: MdTaskAlt,
    tone: "bg-teal-50 text-teal-600 ring-teal-200",
  },
  {
    value: "STEP_PENALTY",
    label: "Step missed",
    hint: "Deducts coins when a booking is completed without Reached, Start or Scan",
    Icon: MdRemoveCircleOutline,
    tone: "bg-red-50 text-red-600 ring-red-200",
    deduction: true,
  },
];
// Judged one booking at a time (no day/week/month target).
const PER_BOOKING_TYPES = ["PER_BOOKING", "ON_TIME_START", "EARLY_START", "ON_TIME_FINISH", "STEP_PENALTY"];
const TIMING_TYPES = ["ON_TIME_START", "EARLY_START", "ON_TIME_FINISH"];
const STEP_OPTIONS = [
  { value: "REACHED", label: "Reached not marked" },
  { value: "STARTED", label: "Start not marked" },
  { value: "SCANNED", label: "Products not scanned (only bookings that used products)" },
];
const stepLabel = (v) => (STEP_OPTIONS.find((s) => s.value === v) || STEP_OPTIONS[0]).label.replace(/ \(.*\)$/, "");
const typeOf = (v) => RULE_TYPES.find((t) => t.value === v) || RULE_TYPES[0];

const PERIODS = [
  { value: "DAY", label: "Day", word: "today" },
  { value: "WEEK", label: "Week", word: "this week" },
  { value: "MONTH", label: "Month", word: "this month" },
];

const EMPTY_RULE = {
  id: null,
  name: "",
  description: "",
  scope: "GLOBAL",
  employeeId: "",
  ruleType: "PER_BOOKING",
  period: "DAY",
  minOrderValue: "",
  targetValue: "",
  rewardCoins: "",
  maxPerDay: "",
  // Punctuality rules: minutes allowed (or, for "Started early", how early).
  graceMinutes: "10",
  // Step deductions: which step is checked.
  step: "REACHED",
  // Slab rule: steps where each reward is the TOTAL for reaching that step.
  tiered: false,
  tiers: [
    { target: "", coins: "" },
    { target: "", coins: "" },
  ],
  startsOn: "",
  endsOn: "",
  isActive: true,
};

const PRESETS = [
  {
    label: "₹10 for starting on time",
    rule: { name: "On-time start", ruleType: "ON_TIME_START", graceMinutes: "10", rewardCoins: "10", tiered: false },
  },
  {
    label: "−₹50 if Reached isn't marked",
    rule: { name: "Mark Reached", ruleType: "STEP_PENALTY", step: "REACHED", rewardCoins: "50", tiered: false },
  },
  {
    label: "₹20 on every ₹1,000+ booking",
    rule: { name: "Big booking bonus", ruleType: "PER_BOOKING", minOrderValue: "1000", rewardCoins: "20", tiered: false },
  },
  {
    label: "₹50 for 5 bookings in a day",
    rule: { name: "5-booking day", ruleType: "BOOKINGS_MILESTONE", period: "DAY", targetValue: "5", rewardCoins: "50", tiered: false },
  },
  {
    label: "Slab: 3 → ₹150, 4 → ₹200, 5 → ₹400",
    rule: {
      name: "Booking slab",
      ruleType: "BOOKINGS_MILESTONE",
      period: "DAY",
      tiered: true,
      tiers: [
        { target: "3", coins: "150" },
        { target: "4", coins: "200" },
        { target: "5", coins: "400" },
      ],
    },
  },
  {
    label: "₹500 for a ₹3,500 day",
    rule: { name: "₹3,500 day", ruleType: "REVENUE_MILESTONE", period: "DAY", targetValue: "3500", rewardCoins: "500", tiered: false },
  },
];

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const count = (n) => Number(n || 0).toLocaleString("en-IN");
const when = (iso) =>
  iso
    ? new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })
    : "";
const shortDate = (d) =>
  d ? new Date(`${d}T00:00:00`).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "";
const periodWord = (p) => (PERIODS.find((x) => x.value === p) || {}).word || "";

// The sentence the professional sees, built from the form so the admin can
// check the rule reads the way they meant it.
// Steps of a slab rule that are filled in, in order.
const cleanTiers = (tiers) =>
  (tiers || [])
    .map((t) => ({ target: Number(t.target), coins: Number(t.coins) }))
    .filter((t) => t.target > 0 && t.coins > 0)
    .sort((a, b) => a.target - b.target);

const isTiered = (r) => (r.tiered !== undefined ? r.tiered : (r.tiers || []).length > 1);

const stepTarget = (r, n) => (r.ruleType === "REVENUE_MILESTONE" ? inr(n) : `${n} bookings`);

const describe = (r) => {
  if (r.ruleType !== "PER_BOOKING" && isTiered(r)) {
    const steps = cleanTiers(r.tiers);
    if (!steps.length) return "Add the steps to see how this reads.";
    const list = steps.map((t) => `${stepTarget(r, t.target)} → ${inr(t.coins)}`).join(", ");
    return `${periodWord(r.period).replace(/^./, (c) => c.toUpperCase())}: ${list}. Each amount is the total, not added up — reaching ${stepTarget(r, steps[steps.length - 1].target)} pays ${inr(steps[steps.length - 1].coins)} in all.`;
  }
  const reward = r.rewardCoins ? inr(r.rewardCoins) : "a reward";
  const grace = r.graceMinutes === "" || r.graceMinutes === null || r.graceMinutes === undefined ? 10 : Number(r.graceMinutes);
  const cap = r.maxPerDay ? `, up to ${r.maxPerDay} a day` : "";
  if (r.ruleType === "ON_TIME_START") {
    return `Earn ${reward} for every booking started no later than ${grace} min after its slot time${cap}.`;
  }
  if (r.ruleType === "EARLY_START") {
    return grace > 0
      ? `Earn ${reward} for every booking started at least ${grace} min before its slot time${cap}.`
      : `Earn ${reward} for every booking started before its slot time${cap}.`;
  }
  if (r.ruleType === "ON_TIME_FINISH") {
    return `Earn ${reward} for every booking finished within its booked time + ${grace} min${cap}.`;
  }
  if (r.ruleType === "STEP_PENALTY") {
    return `${r.rewardCoins ? inr(r.rewardCoins) : "Coins"} deducted from the coin balance for every completed booking where: ${stepLabel(r.step).toLowerCase()}. Refunded if the booking is later cancelled.`;
  }
  if (r.ruleType === "PER_BOOKING") {
    const min = r.minOrderValue ? ` of ${inr(r.minOrderValue)} or more` : "";
    const cap = r.maxPerDay ? `, up to ${r.maxPerDay} a day` : "";
    return `Earn ${reward} for every completed booking${min}${cap}.`;
  }
  const target = r.targetValue
    ? r.ruleType === "REVENUE_MILESTONE"
      ? inr(r.targetValue)
      : `${r.targetValue} bookings`
    : "the target";
  const verb = r.ruleType === "REVENUE_MILESTONE" ? "Earn" : "Complete";
  const min = r.minOrderValue && r.ruleType === "BOOKINGS_MILESTONE" ? ` (each ${inr(r.minOrderValue)}+)` : "";
  return `${verb} ${target}${min} ${periodWord(r.period)} and get ${reward}.`;
};

const intOrNull = (v) => (v === "" || v === null || v === undefined ? null : parseInt(v, 10));

// ------------------------------------------------------------------ bits

const Field = ({ label, hint, children, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="text-[13px] font-medium text-gray-700">{label}</span>
    {children}
    {hint && <span className="block text-xs text-gray-500 mt-1">{hint}</span>}
  </label>
);

const inputCls =
  "mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-400";

const Coin = ({ size = 20 }) => (
  <span
    className="inline-flex items-center justify-center rounded-full shrink-0 text-white shadow-sm"
    style={{
      width: size,
      height: size,
      background: "linear-gradient(135deg,#FFD66B,#FBB034 55%,#D27F00)",
      fontSize: size * 0.55,
    }}
  >
    ★
  </span>
);

const Toggle = ({ checked, onChange, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
      checked ? "bg-emerald-500" : "bg-gray-300"
    }`}
  >
    <span
      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
        checked ? "translate-x-5" : "translate-x-0.5"
      }`}
    />
  </button>
);

const StatTile = ({ Icon, label, value, sub, tone }) => (
  <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex items-start gap-3">
    <span className={`h-10 w-10 rounded-lg flex items-center justify-center text-xl ${tone}`}>
      <Icon />
    </span>
    <div className="min-w-0">
      <div className="text-xs font-medium text-gray-500">{label}</div>
      <div className="text-xl font-semibold text-gray-900 leading-tight">{value}</div>
      {sub && <div className="text-xs text-gray-400 mt-0.5">{sub}</div>}
    </div>
  </div>
);

// How the rule shows up in the professional app.
const AppPreview = ({ form }) => {
  const type = form.ruleType;
  const reward = form.rewardCoins ? inr(form.rewardCoins) : "₹—";
  const target = Number(form.targetValue || 0);
  const progress = Math.round(target * 0.6);
  const money = type === "REVENUE_MILESTONE";
  const fmt = (n) => (money ? inr(n) : count(n));
  return (
    <div className="rounded-[28px] bg-[#F7F6FB] border border-gray-200 p-3 w-full">
      <div className="text-[11px] font-semibold text-gray-400 px-1 mb-2">IN THE APP</div>
      {type === "STEP_PENALTY" ? (
        <div className="bg-white rounded-2xl border border-red-100 p-3 flex items-center gap-3">
          <span className="h-10 w-10 rounded-full bg-red-50 text-red-600 flex items-center justify-center text-xl">
            <MdRemoveCircleOutline />
          </span>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-red-700">
              −{reward} if {stepLabel(form.step).toLowerCase()}
            </div>
            <div className="text-xs text-gray-500">Taken from coins · refunded if the booking is cancelled</div>
          </div>
        </div>
      ) : TIMING_TYPES.includes(type) ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-3 flex items-center gap-3">
          <span className={`h-10 w-10 rounded-full flex items-center justify-center text-xl ${typeOf(type).tone}`}>
            {React.createElement(typeOf(type).Icon)}
          </span>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-gray-900">
              {reward} for {type === "ON_TIME_FINISH" ? "finishing on time" : type === "EARLY_START" ? "starting early" : "starting on time"}
            </div>
            <div className="text-xs text-gray-500">
              {type === "EARLY_START"
                ? (Number(form.graceMinutes) > 0 ? `At least ${form.graceMinutes} min before the slot` : "Any time before the slot")
                : `Within ${form.graceMinutes === "" ? 10 : form.graceMinutes} min · credited automatically`}
            </div>
          </div>
        </div>
      ) : type !== "PER_BOOKING" && isTiered(form) ? (
        <LadderPreview form={form} />
      ) : type === "PER_BOOKING" ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-3 flex items-center gap-3">
          <span className="h-10 w-10 rounded-full bg-amber-50 text-amber-600 flex items-center justify-center text-xl">
            <MdBolt />
          </span>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-gray-900">
              {reward} on every booking{form.minOrderValue ? ` of ${inr(form.minOrderValue)}+` : ""}
            </div>
            <div className="text-xs text-gray-500">
              Credited automatically{form.maxPerDay ? ` · Up to ${form.maxPerDay} times a day` : ""}
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 p-4">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[11px] font-bold tracking-wider text-amber-700">
              {form.period === "WEEK" ? "THIS WEEK'S" : form.period === "MONTH" ? "THIS MONTH'S" : "TODAY'S"} TARGET
            </span>
            <span className="text-[11px] font-bold text-amber-700 bg-amber-50 rounded-full px-2 py-0.5">
              {reward} bonus
            </span>
          </div>
          <div className="mt-2">
            <span className="text-2xl font-extrabold text-gray-900">{target ? fmt(progress) : "—"}</span>
            <span className="text-sm font-semibold text-gray-400"> / {target ? fmt(target) : "—"}</span>
          </div>
          <div className="text-xs text-gray-500">{form.name || "Rule name"}</div>
          <div className="mt-3 h-2.5 rounded-full bg-violet-50 overflow-hidden">
            <div className="h-full w-3/5 rounded-full bg-gradient-to-r from-violet-400 to-violet-600" />
          </div>
          <div className="mt-2 text-xs font-semibold text-gray-800">
            {target ? `${fmt(target - progress)} more to unlock ${reward}` : "Set a target to preview"}
          </div>
        </div>
      )}
      <div className="text-[11px] text-gray-400 px-1 mt-2">Sample progress shown.</div>
    </div>
  );
};

// ---------------------------------------------------------------- slabs

// "3 bookings → ₹150 · 4 → ₹200 · 5 → ₹400" as compact chips.
const TierChips = ({ rule }) => {
  const steps = cleanTiers(rule.tiers);
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {steps.map((t, i) => (
        <React.Fragment key={t.target}>
          {i > 0 && <span className="text-gray-300 text-xs">›</span>}
          <span className="inline-flex items-center gap-1 rounded-full bg-violet-50 text-violet-800 text-xs font-medium px-2 py-0.5">
            {rule.ruleType === "REVENUE_MILESTONE" ? inr(t.target) : t.target}
            <span className="text-violet-400">→</span>
            <span className="font-bold">{inr(t.coins)}</span>
          </span>
        </React.Fragment>
      ))}
    </div>
  );
};

// Edits the steps and shows, in plain numbers, what actually gets
// credited - so nobody reads 150 / 200 / 400 as 750.
const TierEditor = ({ form, onChange, onAdd, onRemove }) => {
  const money = form.ruleType === "REVENUE_MILESTONE";
  const steps = cleanTiers(form.tiers);
  const problems = [];
  form.tiers.forEach((t, i) => {
    if (i === 0) return;
    const prev = form.tiers[i - 1];
    if (t.target && prev.target && Number(t.target) <= Number(prev.target)) {
      problems.push(`Step ${i + 1} must need more than step ${i}.`);
    }
    if (t.coins && prev.coins && Number(t.coins) <= Number(prev.coins)) {
      problems.push(`Step ${i + 1}'s total must be more than step ${i}'s.`);
    }
  });

  return (
    <div className="rounded-xl border border-violet-200 bg-violet-50/30 p-3">
      <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
        <MdStairs className="text-violet-600 text-lg" /> Steps
      </div>
      <p className="text-xs text-gray-600 mt-0.5 mb-3">
        Each reward is the <span className="font-semibold">total</span> for reaching that step. When a professional moves
        up a step, only the difference is added.
      </p>
      <div className="space-y-2">
        {form.tiers.map((t, i) => (
          <div key={i} className="flex items-center gap-2">
            <span className="h-6 w-6 rounded-full bg-violet-600 text-white text-xs font-bold flex items-center justify-center shrink-0">
              {i + 1}
            </span>
            <span className="text-sm text-gray-600 shrink-0">Reach</span>
            <input
              type="number"
              min="1"
              required
              aria-label={`Step ${i + 1} target`}
              className="w-24 border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white"
              value={t.target}
              onChange={(e) => onChange(i, "target", e.target.value)}
              placeholder={money ? "₹" : "#"}
            />
            <span className="text-sm text-gray-600 shrink-0">{money ? "earned" : "bookings"} → total</span>
            <div className="relative">
              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-sm text-gray-400">₹</span>
              <input
                type="number"
                min="1"
                required
                aria-label={`Step ${i + 1} total reward`}
                className="w-24 border border-gray-200 rounded-lg pl-5 pr-2 py-1.5 text-sm bg-white"
                value={t.coins}
                onChange={(e) => onChange(i, "coins", e.target.value)}
              />
            </div>
            {form.tiers.length > 2 && (
              <button
                type="button"
                onClick={() => onRemove(i)}
                className="p-1 rounded-md text-gray-400 hover:bg-red-50 hover:text-red-600"
                aria-label={`Remove step ${i + 1}`}
              >
                <MdClose />
              </button>
            )}
          </div>
        ))}
      </div>
      {form.tiers.length < 6 && (
        <button type="button" onClick={onAdd} className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-violet-700 hover:underline">
          <MdAdd /> Add step
        </button>
      )}
      {problems.length > 0 && <div className="mt-2 text-xs text-red-600">{problems[0]}</div>}

      {steps.length > 1 && problems.length === 0 && (
        <div className="mt-3 rounded-lg bg-white border border-gray-100 p-2.5">
          <div className="text-[11px] font-semibold tracking-wide text-gray-500 mb-1.5">WHAT GETS CREDITED</div>
          <table className="w-full text-xs">
            <tbody>
              {steps.map((t, i) => (
                <tr key={t.target} className="text-gray-700">
                  <td className="py-0.5">On reaching {money ? inr(t.target) : `${t.target} bookings`}</td>
                  <td className="py-0.5 text-right font-semibold text-emerald-700">
                    +{inr(t.coins - (i ? steps[i - 1].coins : 0))}
                  </td>
                  <td className="py-0.5 text-right text-gray-500 w-28">total {inr(t.coins)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-1.5 pt-1.5 border-t border-gray-100 text-xs font-semibold text-gray-900">
            Maximum: {inr(steps[steps.length - 1].coins)} {periodWord(form.period)} — not{" "}
            <span className="line-through text-gray-400">{inr(steps.reduce((s, t) => s + t.coins, 0))}</span>
          </div>
        </div>
      )}
    </div>
  );
};

// Sample: the professional has just reached the second-to-last step.
const LadderPreview = ({ form }) => {
  const steps = cleanTiers(form.tiers);
  const money = form.ruleType === "REVENUE_MILESTONE";
  const fmt = (n) => (money ? inr(n) : count(n));
  if (steps.length < 2) {
    return <div className="bg-white rounded-2xl border border-gray-100 p-4 text-xs text-gray-500">Add steps to preview.</div>;
  }
  const current = steps[steps.length - 2].target;
  const next = steps[steps.length - 1];
  const prevTotal = steps[steps.length - 2].coins;
  const max = next.target;
  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-4">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] font-bold tracking-wider text-amber-700">
          {form.period === "WEEK" ? "THIS WEEK'S" : form.period === "MONTH" ? "THIS MONTH'S" : "TODAY'S"} TARGET
        </span>
        <span className="text-[11px] font-bold text-emerald-700 bg-emerald-50 rounded-full px-2 py-0.5">
          {inr(prevTotal)} earned
        </span>
      </div>
      <div className="mt-2">
        <span className="text-2xl font-extrabold text-gray-900">{fmt(current)}</span>
        <span className="text-sm font-semibold text-gray-400"> {money ? "" : "bookings"}</span>
      </div>
      <div className="relative mt-5 mb-7 mx-2">
        <div className="h-2 rounded-full bg-violet-50" />
        <div
          className="absolute top-0 left-0 h-2 rounded-full bg-gradient-to-r from-violet-400 to-violet-600"
          style={{ width: `${(current / max) * 100}%` }}
        />
        {steps.map((t) => {
          const reached = current >= t.target;
          return (
            <div key={t.target} className="absolute -top-2 -translate-x-1/2 flex flex-col items-center" style={{ left: `${(t.target / max) * 100}%` }}>
              <span
                className={`h-6 w-6 rounded-full border-2 flex items-center justify-center text-[10px] font-bold ${
                  reached ? "bg-violet-600 border-violet-600 text-white" : "bg-white border-amber-400 text-amber-700"
                }`}
              >
                {reached ? "✓" : fmt(t.target).replace("₹", "")}
              </span>
              <span className={`mt-0.5 text-[10px] font-bold ${reached ? "text-violet-700" : "text-amber-700"}`}>{inr(t.coins)}</span>
            </div>
          );
        })}
      </div>
      <div className="text-xs font-semibold text-gray-800">
        {money ? inr(next.target - current) : count(next.target - current)} more → {inr(next.coins)} total (+{inr(next.coins - prevTotal)})
      </div>
      <div className="text-[11px] text-gray-500 mt-0.5">Amounts are totals, not added up.</div>
    </div>
  );
};

// ------------------------------------------------------------- rule form

const RuleForm = ({ initial, employees, onClose, onSaved }) => {
  const [form, setForm] = useState(initial);
  const [error, setError] = useState("");
  const [save, { loading }] = useMutation(SAVE_REWARD_RULE);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const milestone = !PER_BOOKING_TYPES.includes(form.ruleType);
  const penalty = form.ruleType === "STEP_PENALTY";
  const timed = TIMING_TYPES.includes(form.ruleType);
  const tiered = milestone && form.tiered;
  const setTier = (i, k, v) =>
    setForm((f) => ({ ...f, tiers: f.tiers.map((t, j) => (j === i ? { ...t, [k]: v } : t)) }));
  const addTier = () => setForm((f) => ({ ...f, tiers: [...f.tiers, { target: "", coins: "" }] }));
  const removeTier = (i) => setForm((f) => ({ ...f, tiers: f.tiers.filter((_, j) => j !== i) }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const data = {
      id: form.id || undefined,
      name: form.name,
      description: form.description || null,
      scope: form.scope,
      employeeId: form.scope === "EMPLOYEE" ? form.employeeId : null,
      ruleType: form.ruleType,
      period: milestone ? form.period : null,
      minOrderValue: penalty ? null : intOrNull(form.minOrderValue),
      targetValue: milestone && !tiered ? intOrNull(form.targetValue) : null,
      rewardCoins: tiered ? null : intOrNull(form.rewardCoins),
      tiers: tiered
        ? form.tiers.map((t) => ({ target: intOrNull(t.target), coins: intOrNull(t.coins) }))
        : null,
      maxPerDay: !milestone && !penalty ? intOrNull(form.maxPerDay) : null,
      graceMinutes: timed ? intOrNull(form.graceMinutes) : null,
      step: penalty ? form.step : null,
      startsOn: form.startsOn || null,
      endsOn: form.endsOn || null,
      isActive: form.isActive,
    };
    try {
      const res = await save({ variables: { data } });
      const out = res.data?.saveRewardRule;
      if (out?.status !== "SUCCESS") {
        setError(out?.message || "Could not save");
        return;
      }
      onSaved();
    } catch (err) {
      setError("Could not save. Please try again.");
    }
  };

  return (
    <Modal onClose={onClose} disableOutsideClick widthClass="sm:w-[920px]">
      <form onSubmit={submit}>
        <div className="flex items-start justify-between gap-4 mb-5">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{form.id ? "Edit reward rule" : "New reward rule"}</h2>
            <p className="text-sm text-gray-500">Coins are credited automatically when a booking completes.</p>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded-md text-gray-400 hover:bg-gray-100" aria-label="Close">
            <MdClose className="text-xl" />
          </button>
        </div>

        <div className="grid md:grid-cols-[1fr_280px] gap-6">
          <div className="space-y-5">
            {!form.id && (
              <div>
                <div className="text-[13px] font-medium text-gray-700 mb-1.5">Start from an example</div>
                <div className="flex flex-wrap gap-2">
                  {PRESETS.map((p) => (
                    <button
                      key={p.label}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, ...p.rule }))}
                      className="text-xs px-3 py-1.5 rounded-full border border-gray-200 bg-white hover:border-violet-300 hover:bg-violet-50"
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div>
              {[
                ["Reward is given for", RULE_TYPES.filter((t) => !t.deduction)],
                ["Or deduct coins for", RULE_TYPES.filter((t) => t.deduction)],
              ].map(([heading, types]) => (
              <div key={heading} className="mb-3">
              <div className="text-[13px] font-medium text-gray-700 mb-1.5">{heading}</div>
              <div className="grid sm:grid-cols-3 gap-2">
                {types.map((t) => {
                  const on = form.ruleType === t.value;
                  return (
                    <button
                      key={t.value}
                      type="button"
                      onClick={() => set("ruleType", t.value)}
                      className={`text-left rounded-xl border p-3 transition ${
                        on ? "border-violet-500 ring-2 ring-violet-100 bg-violet-50/40" : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <span className={`h-8 w-8 rounded-lg flex items-center justify-center text-lg ${t.tone}`}>
                        <t.Icon />
                      </span>
                      <div className="mt-2 text-sm font-semibold text-gray-900">{t.label}</div>
                      <div className="text-xs text-gray-500 leading-snug">{t.hint}</div>
                    </button>
                  );
                })}
              </div>
              </div>
              ))}
            </div>

            <Field label="Rule name">
              <input className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Big booking bonus" required />
            </Field>

            <div className="grid sm:grid-cols-2 gap-4">
              {milestone && (
                <Field label="Within">
                  <div className="mt-1 grid grid-cols-3 rounded-lg border border-gray-200 p-0.5 bg-gray-50">
                    {PERIODS.map((p) => (
                      <button
                        key={p.value}
                        type="button"
                        onClick={() => set("period", p.value)}
                        className={`text-sm py-1.5 rounded-md ${
                          form.period === p.value ? "bg-white shadow-sm font-semibold text-violet-700" : "text-gray-600"
                        }`}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                </Field>
              )}
              {milestone && (
                <Field label="Reward structure">
                  <div className="mt-1 grid grid-cols-2 rounded-lg border border-gray-200 p-0.5 bg-gray-50">
                    {[
                      [false, "One target"],
                      [true, "Steps (slab)"],
                    ].map(([v, l]) => (
                      <button
                        key={l}
                        type="button"
                        onClick={() => set("tiered", v)}
                        className={`text-sm py-1.5 rounded-md ${
                          form.tiered === v ? "bg-white shadow-sm font-semibold text-violet-700" : "text-gray-600"
                        }`}
                      >
                        {l}
                      </button>
                    ))}
                  </div>
                </Field>
              )}
              {milestone && !tiered && (
                <Field label={form.ruleType === "REVENUE_MILESTONE" ? "Target amount (₹)" : "Number of bookings"}>
                  <input type="number" min="1" className={inputCls} value={form.targetValue} onChange={(e) => set("targetValue", e.target.value)} required />
                </Field>
              )}
              {penalty && (
                <Field label="Deduct when">
                  <select className={inputCls} value={form.step} onChange={(e) => set("step", e.target.value)}>
                    {STEP_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </Field>
              )}
              {!tiered && (
                <Field
                  label={penalty ? "Deduct (coins = ₹)" : "Reward (coins = ₹)"}
                  hint={penalty ? "Taken from the professional's coin balance, per booking." : undefined}
                >
                  <input type="number" min="1" className={`${inputCls} ${penalty ? "text-red-700" : ""}`} value={form.rewardCoins} onChange={(e) => set("rewardCoins", e.target.value)} required />
                </Field>
              )}
              {timed && (
                <Field
                  label={form.ruleType === "EARLY_START" ? "At least this many minutes early" : "Grace minutes"}
                  hint={
                    form.ruleType === "EARLY_START"
                      ? "0 = any time before the slot."
                      : form.ruleType === "ON_TIME_FINISH"
                        ? "Minutes allowed after the booked end time."
                        : "Minutes allowed after the slot time."
                  }
                >
                  <input type="number" min="0" max="240" className={inputCls} value={form.graceMinutes} onChange={(e) => set("graceMinutes", e.target.value)} required />
                </Field>
              )}
              {tiered && (
                <div className="sm:col-span-2">
                  <TierEditor
                    form={form}
                    onChange={setTier}
                    onAdd={addTier}
                    onRemove={removeTier}
                  />
                </div>
              )}
              {!penalty && (!milestone || form.ruleType === "BOOKINGS_MILESTONE") && (
                <Field
                  label="Only bookings of at least (₹)"
                  hint={milestone ? "Smaller bookings don't count. Empty = all." : "Empty = any booking value."}
                >
                  <input type="number" min="1" className={inputCls} value={form.minOrderValue} onChange={(e) => set("minOrderValue", e.target.value)} />
                </Field>
              )}
              {!milestone && !penalty && (
                <Field label="Daily limit" hint="Most times this pays in a day. Empty = no limit.">
                  <input type="number" min="1" className={inputCls} value={form.maxPerDay} onChange={(e) => set("maxPerDay", e.target.value)} />
                </Field>
              )}
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Applies to">
                <select className={inputCls} value={form.scope} onChange={(e) => set("scope", e.target.value)}>
                  <option value="GLOBAL">Every professional</option>
                  <option value="EMPLOYEE">One professional</option>
                </select>
              </Field>
              {form.scope === "EMPLOYEE" && (
                <Field label="Professional">
                  <select className={inputCls} value={form.employeeId} onChange={(e) => set("employeeId", e.target.value)} required>
                    <option value="">Select…</option>
                    {employees.map((e) => (
                      <option key={e.id} value={e.id}>
                        {e.fullName}
                      </option>
                    ))}
                  </select>
                </Field>
              )}
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Starts on" hint="Optional">
                <input type="date" className={inputCls} value={form.startsOn || ""} onChange={(e) => set("startsOn", e.target.value)} />
              </Field>
              <Field label="Ends on" hint="Optional">
                <input type="date" className={inputCls} value={form.endsOn || ""} onChange={(e) => set("endsOn", e.target.value)} />
              </Field>
            </div>
          </div>

          <div className="space-y-4 md:sticky md:top-0 self-start">
            <AppPreview form={form} />
            <div className="rounded-xl bg-amber-50 border border-amber-100 px-3 py-2.5 text-sm text-gray-800">
              <div className="text-[11px] font-semibold tracking-wide text-amber-700 mb-0.5">RULE IN ONE LINE</div>
              {describe(form)}
            </div>
            <div className="flex items-center justify-between rounded-xl border border-gray-200 px-3 py-2.5">
              <div>
                <div className="text-sm font-medium text-gray-900">{form.isActive ? "Live" : "Paused"}</div>
                <div className="text-xs text-gray-500">{form.isActive ? "Pays from the next booking" : "Saved but not paying"}</div>
              </div>
              <Toggle checked={form.isActive} onChange={(v) => set("isActive", v)} label="Live" />
            </div>
          </div>
        </div>

        {error && <div className="mt-4 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</div>}

        <div className="flex justify-end gap-2 pt-5 mt-5 border-t border-gray-100">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50">
            Cancel
          </button>
          <button type="submit" disabled={loading} className="px-5 py-2 rounded-lg bg-[#FBB033] text-black font-semibold hover:brightness-95 disabled:opacity-60">
            {loading ? "Saving…" : form.id ? "Save changes" : "Create rule"}
          </button>
        </div>
      </form>
    </Modal>
  );
};

// ------------------------------------------------------------ adjust form

const AdjustForm = ({ employees, onClose, onSaved }) => {
  const [employeeId, setEmployeeId] = useState("");
  const [direction, setDirection] = useState("GIVE");
  const [coins, setCoins] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  const [adjust, { loading }] = useMutation(ADJUST_REWARD_COINS);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const amount = Math.abs(parseInt(coins, 10) || 0) * (direction === "GIVE" ? 1 : -1);
    try {
      const res = await adjust({ variables: { data: { employeeId, coins: amount, note } } });
      const out = res.data?.adjustRewardCoins;
      if (out?.status !== "SUCCESS") {
        setError(out?.message || "Could not update");
        return;
      }
      onSaved();
    } catch (err) {
      setError("Could not update. Please try again.");
    }
  };

  return (
    <Modal onClose={onClose} disableOutsideClick widthClass="sm:w-[460px]">
      <form onSubmit={submit} className="space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Give or take back coins</h2>
            <p className="text-sm text-gray-500">For one-off bonuses or corrections.</p>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded-md text-gray-400 hover:bg-gray-100" aria-label="Close">
            <MdClose className="text-xl" />
          </button>
        </div>
        <div className="grid grid-cols-2 rounded-lg border border-gray-200 p-0.5 bg-gray-50">
          {[
            ["GIVE", "Give coins"],
            ["TAKE", "Take back"],
          ].map(([v, l]) => (
            <button
              key={v}
              type="button"
              onClick={() => setDirection(v)}
              className={`text-sm py-1.5 rounded-md ${
                direction === v ? `bg-white shadow-sm font-semibold ${v === "GIVE" ? "text-emerald-700" : "text-red-600"}` : "text-gray-600"
              }`}
            >
              {l}
            </button>
          ))}
        </div>
        <Field label="Professional">
          <select className={inputCls} value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} required>
            <option value="">Select…</option>
            {employees.map((e) => (
              <option key={e.id} value={e.id}>
                {e.fullName}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Coins">
          <input type="number" min="1" className={inputCls} value={coins} onChange={(e) => setCoins(e.target.value)} required />
        </Field>
        <Field label="Reason" hint="The professional sees this in their coin history.">
          <input className={inputCls} value={note} onChange={(e) => setNote(e.target.value)} maxLength={240} required />
        </Field>
        {error && <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50">
            Cancel
          </button>
          <button type="submit" disabled={loading} className="px-5 py-2 rounded-lg bg-[#FBB033] text-black font-semibold hover:brightness-95 disabled:opacity-60">
            {loading ? "Saving…" : direction === "GIVE" ? `Give ${coins || ""} coins` : `Take back ${coins || ""} coins`}
          </button>
        </div>
      </form>
    </Modal>
  );
};

// -------------------------------------------------------------- rule card

const RuleCard = ({ rule: r, onEdit, onToggle, onDelete }) => {
  const t = typeOf(r.ruleType);
  return (
    <div className={`bg-white rounded-xl border shadow-sm p-4 flex flex-col ${r.isActive ? "border-gray-100" : "border-dashed border-gray-200 opacity-75"}`}>
      <div className="flex items-start gap-3">
        <span className={`h-10 w-10 rounded-lg flex items-center justify-center text-xl shrink-0 ring-1 ${t.tone}`}>
          <t.Icon />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-gray-900 truncate">{r.name}</h3>
            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-gray-600 bg-gray-100 rounded-full px-2 py-0.5">
              {r.scope === "GLOBAL" ? <MdGroups /> : <MdPerson />}
              {r.scope === "GLOBAL" ? "Everyone" : r.employeeName || "One professional"}
            </span>
          </div>
          {isTiered(r) ? (
            <div className="mt-1.5">
              <TierChips rule={r} />
              <p className="text-xs text-gray-500 mt-1.5">
                Amounts are totals, not added up · {periodWord(r.period)}
              </p>
            </div>
          ) : (
            <p className="text-sm text-gray-600 mt-0.5">{r.description || describe(r)}</p>
          )}
          {(r.startsOn || r.endsOn) && (
            <p className="text-xs text-gray-400 mt-1">
              {r.startsOn ? shortDate(r.startsOn) : "Now"} → {r.endsOn ? shortDate(r.endsOn) : "no end"}
            </p>
          )}
        </div>
        <div className="text-right shrink-0">
          <div className="flex items-center gap-1.5 justify-end">
            <Coin size={18} />
            <span className={`text-lg font-bold ${r.ruleType === "STEP_PENALTY" ? "text-red-600" : "text-gray-900"}`}>
              {r.ruleType === "STEP_PENALTY" ? "−" : ""}
              {count(r.rewardCoins)}
            </span>
          </div>
          <div className="text-[11px] text-gray-400">
            {PER_BOOKING_TYPES.includes(r.ruleType)
              ? "per booking"
              : isTiered(r)
                ? `max per ${(r.period || "day").toLowerCase()}`
                : `per ${(r.period || "day").toLowerCase()}`}
          </div>
        </div>
      </div>
      <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between gap-3">
        <div className="text-xs text-gray-500">
          {r.timesAwarded > 0 ? (
            <>
              {r.ruleType === "STEP_PENALTY" ? "Deducted" : "Paid"}{" "}
              <span className="font-semibold text-gray-800">{count(r.timesAwarded)}×</span> ·{" "}
              <span className={`font-semibold ${r.ruleType === "STEP_PENALTY" ? "text-red-600" : "text-gray-800"}`}>
                {inr(Math.abs(r.coinsAwarded))}
              </span>
            </>
          ) : (
            "Not paid out yet"
          )}
        </div>
        <div className="flex items-center gap-1">
          <span className={`text-xs font-medium mr-1 ${r.isActive ? "text-emerald-700" : "text-gray-500"}`}>
            {r.isActive ? "Live" : "Paused"}
          </span>
          <Toggle checked={r.isActive} onChange={onToggle} label={r.isActive ? "Pause rule" : "Resume rule"} />
          <button onClick={onEdit} className="ml-2 p-1.5 rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-800" title="Edit">
            <MdEdit />
          </button>
          <button
            onClick={onDelete}
            disabled={r.timesAwarded > 0}
            className="p-1.5 rounded-md text-gray-500 hover:bg-red-50 hover:text-red-600 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-gray-500"
            title={r.timesAwarded > 0 ? "Already paid out - pause it instead" : "Delete"}
          >
            <MdDeleteOutline />
          </button>
        </div>
      </div>
    </div>
  );
};

const ENTRY_STYLE = {
  EARN: { label: "Earned", cls: "bg-amber-50 text-amber-700" },
  REDEEM: { label: "Redeemed", cls: "bg-violet-50 text-violet-700" },
  ADJUST: { label: "Adjustment", cls: "bg-sky-50 text-sky-700" },
  REVERSAL: { label: "Reversed", cls: "bg-red-50 text-red-700" },
};

// -------------------------------------------------------------------- page

const RewardsManagement = () => {
  const [tab, setTab] = useState("rules");
  const [editing, setEditing] = useState(null);
  const [adjusting, setAdjusting] = useState(false);
  const [ledgerEmployee, setLedgerEmployee] = useState("");

  const rulesQuery = useQuery(GET_REWARD_RULES, { fetchPolicy: "network-only" });
  const ledgerQuery = useQuery(GET_REWARD_LEDGER, {
    variables: { employeeId: ledgerEmployee || undefined, limit: 100, offset: 0 },
    fetchPolicy: "network-only",
    skip: tab !== "activity",
  });
  const empQuery = useQuery(GET_EMPLOYEES, { variables: { data: {} } });
  const employees = useMemo(
    () => [...(empQuery.data?.getEmployeelist?.employee || [])].sort((a, b) => (a.fullName || "").localeCompare(b.fullName || "")),
    [empQuery.data],
  );

  const [setActive] = useMutation(SET_REWARD_RULE_ACTIVE);
  const [deleteRule] = useMutation(DELETE_REWARD_RULE);

  const rules = rulesQuery.data?.getRewardRules?.rules || [];
  const overview = rulesQuery.data?.getRewardRules?.overview;
  const ledger = ledgerQuery.data?.getRewardLedger;

  const toggle = async (rule) => {
    await setActive({ variables: { id: rule.id, isActive: !rule.isActive } });
    rulesQuery.refetch();
  };

  const remove = async (rule) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete "${rule.name}"?`)) return;
    const res = await deleteRule({ variables: { id: rule.id } });
    const out = res.data?.deleteRewardRule;
    // eslint-disable-next-line no-alert
    if (out?.status !== "SUCCESS") window.alert(out?.message || "Could not delete");
    rulesQuery.refetch();
  };

  const openEdit = (rule) =>
    setEditing({
      ...EMPTY_RULE,
      ...rule,
      description: rule.description || "",
      employeeId: rule.employeeId || "",
      period: rule.period || "DAY",
      minOrderValue: rule.minOrderValue ?? "",
      targetValue: rule.targetValue ?? "",
      maxPerDay: rule.maxPerDay ?? "",
      graceMinutes: rule.graceMinutes ?? "10",
      step: rule.step || "REACHED",
      tiered: (rule.tiers || []).length > 1,
      tiers:
        (rule.tiers || []).length > 1
          ? rule.tiers.map((t) => ({ target: String(t.target), coins: String(t.coins) }))
          : EMPTY_RULE.tiers,
      startsOn: rule.startsOn || "",
      endsOn: rule.endsOn || "",
    });

  return (
    <div className="min-h-screen flex">
      <Sidebar />
      <Layout>
        <div className="max-w-6xl">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-semibold text-white">Professional rewards</h1>
              <p className="text-sm text-gray-400 mt-0.5">
                Rules that pay coins (1 coin = ₹1). Professionals redeem coins into their monthly payout.
              </p>
            </div>
            <div className="flex gap-2 self-start sm:self-auto">
              <button
                onClick={() => setAdjusting(true)}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-white/15 bg-white/5 text-white hover:bg-white/10"
              >
                <MdCardGiftcard /> Give / take coins
              </button>
              <button
                onClick={() => setEditing({ ...EMPTY_RULE })}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#FBB033] text-black rounded-lg font-semibold hover:brightness-95"
              >
                <MdAdd className="text-lg" /> New rule
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            <StatTile Icon={MdEmojiEvents} tone="bg-violet-50 text-violet-600" label="Live rules" value={overview ? count(overview.liveRules) : "—"} sub={`${rules.length} total`} />
            <StatTile Icon={MdTrendingUp} tone="bg-emerald-50 text-emerald-600" label="Earned this month" value={overview ? inr(overview.coinsThisMonth) : "—"} />
            <StatTile Icon={MdGroups} tone="bg-sky-50 text-sky-600" label="Professionals earning" value={overview ? count(overview.earnersThisMonth) : "—"} sub="this month" />
            <StatTile Icon={MdSavings} tone="bg-amber-50 text-amber-600" label="Owed (not redeemed)" value={overview ? inr(overview.outstandingCoins) : "—"} sub="will reach payouts" />
          </div>

          <div className="inline-flex rounded-lg border border-white/10 bg-white/5 p-0.5 mb-4">
            {[
              ["rules", "Rules"],
              ["activity", "Coin activity"],
              ["eotm", "Employee of the Month"],
              ["game", "XP & badges"],
            ].map(([key, label]) => (
              <button
                key={key}
                onClick={() => setTab(key)}
                className={`px-4 py-1.5 rounded-md text-sm ${tab === key ? "bg-white shadow-sm font-semibold text-gray-900" : "text-gray-300 hover:text-white"}`}
              >
                {label}
              </button>
            ))}
          </div>

          {tab === "rules" && (
            <>
              {rulesQuery.loading && <div className="py-16 text-center text-gray-500">Loading…</div>}
              {!rulesQuery.loading && rules.length === 0 && (
                <div className="bg-white rounded-xl border border-dashed border-gray-300 py-12 px-6 text-center">
                  <div className="mx-auto h-14 w-14 rounded-full bg-amber-50 text-amber-600 flex items-center justify-center text-3xl">
                    <MdEmojiEvents />
                  </div>
                  <h3 className="mt-3 font-semibold text-gray-900">No reward rules yet</h3>
                  <p className="text-sm text-gray-500 mt-1">Start with one of these, or build your own.</p>
                  <div className="mt-4 flex flex-wrap justify-center gap-2">
                    {PRESETS.map((p) => (
                      <button
                        key={p.label}
                        onClick={() => setEditing({ ...EMPTY_RULE, ...p.rule })}
                        className="text-sm px-3 py-1.5 rounded-full border border-gray-200 hover:border-violet-300 hover:bg-violet-50"
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {rules.length > 0 && (
                <>
                  <div className="grid lg:grid-cols-2 gap-3">
                    {rules.map((r) => (
                      <RuleCard key={r.id} rule={r} onEdit={() => openEdit(r)} onToggle={() => toggle(r)} onDelete={() => remove(r)} />
                    ))}
                  </div>
                  <p className="text-xs text-gray-400 mt-3">A booking can satisfy several rules at once; each pays on its own.</p>
                </>
              )}
            </>
          )}

          {tab === "game" && <GameRulesTab />}
          {tab === "eotm" && <EotmTab />}

          {tab === "activity" && (
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-gray-100">
                <p className="text-sm text-gray-600">
                  {ledger && (
                    <>
                      {ledgerEmployee ? "This professional's balance" : "Owed to professionals"}:{" "}
                      <span className="font-semibold text-gray-900">{inr(ledger.outstandingCoins)}</span>
                      <span className="text-gray-400"> · {count(ledger.total)} entries</span>
                    </>
                  )}
                </p>
                <select
                  value={ledgerEmployee}
                  onChange={(e) => setLedgerEmployee(e.target.value)}
                  className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white"
                >
                  <option value="">All professionals</option>
                  {employees.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.fullName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="text-left text-xs uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-4 py-2.5 font-medium">When</th>
                      <th className="px-4 py-2.5 font-medium">Professional</th>
                      <th className="px-4 py-2.5 font-medium">What</th>
                      <th className="px-4 py-2.5 font-medium">Type</th>
                      <th className="px-4 py-2.5 font-medium text-right">Coins</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ledgerQuery.loading && (
                      <tr>
                        <td colSpan={5} className="px-4 py-10 text-center text-gray-500">
                          Loading…
                        </td>
                      </tr>
                    )}
                    {!ledgerQuery.loading && (ledger?.entries || []).length === 0 && (
                      <tr>
                        <td colSpan={5} className="px-4 py-10 text-center text-gray-500">
                          No coin activity yet.
                        </td>
                      </tr>
                    )}
                    {(ledger?.entries || []).map((e) => {
                      const st = ENTRY_STYLE[e.entryType] || ENTRY_STYLE.ADJUST;
                      return (
                        <tr key={e.id} className="border-t border-gray-100 hover:bg-gray-50/60">
                          <td className="px-4 py-2.5 whitespace-nowrap text-gray-600">{when(e.createdAt)}</td>
                          <td className="px-4 py-2.5 font-medium text-gray-900">{e.employeeName || "—"}</td>
                          <td className="px-4 py-2.5">
                            <div className="text-gray-900">{e.title}</div>
                            {e.note && <div className="text-xs text-gray-500">{e.note}</div>}
                          </td>
                          <td className="px-4 py-2.5">
                            <span className={`text-xs font-medium rounded-full px-2 py-0.5 ${st.cls}`}>{st.label}</span>
                          </td>
                          <td className={`px-4 py-2.5 text-right font-semibold tabular-nums ${e.coins < 0 ? "text-red-600" : "text-emerald-700"}`}>
                            {e.coins > 0 ? "+" : "−"}
                            {count(Math.abs(e.coins))}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {editing && (
          <RuleForm
            initial={editing}
            employees={employees}
            onClose={() => setEditing(null)}
            onSaved={() => {
              setEditing(null);
              rulesQuery.refetch();
            }}
          />
        )}
        {adjusting && (
          <AdjustForm
            employees={employees}
            onClose={() => setAdjusting(false)}
            onSaved={() => {
              setAdjusting(false);
              ledgerQuery.refetch();
              rulesQuery.refetch();
            }}
          />
        )}
      </Layout>
    </div>
  );
};

export default RewardsManagement;
